package diana.soleil.design.iraniancanadienbazzar.feature.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppSearchBar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_message_empty
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_message_hint
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_message_search_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_message_title
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_group_create
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_group_name_label
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_group_name_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.new_group_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * "New message" — find a user by name and start a DM (a message request). Guests are bounced to
 * sign-in; picking a person creates/reopens the thread and navigates into it via [onOpenConversation].
 */
@Composable
fun NewMessageRoute(
    onBack: () -> Unit,
    onOpenConversation: (conversationId: String) -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: NewMessageViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    LaunchedEffect(state.signInRequired) { if (state.signInRequired) onRequireSignIn() }
    LaunchedEffect(state.openConversationId) {
        val id = state.openConversationId ?: return@LaunchedEffect
        viewModel.consumeOpen()
        onOpenConversation(id)
    }
    NewMessageScreen(
        state = state,
        onBack = onBack,
        onQueryChange = viewModel::onQueryChange,
        onPickUser = viewModel::startDm,
        onToggleGroupMode = viewModel::toggleGroupMode,
        onGroupTitleChange = viewModel::setGroupTitle,
        onToggleGroupMember = viewModel::toggleGroupMember,
        onCreateGroup = viewModel::createGroup,
        modifier = modifier,
    )
}

@Composable
fun NewMessageScreen(
    state: NewMessageUiState,
    onBack: () -> Unit,
    onQueryChange: (String) -> Unit,
    onPickUser: (PublicProfile) -> Unit,
    onToggleGroupMode: () -> Unit = {},
    onGroupTitleChange: (String) -> Unit = {},
    onToggleGroupMember: (PublicProfile) -> Unit = {},
    onCreateGroup: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .background(AppTheme.colors.background)
            .appContentWidth(AppTheme.sizes.contentMaxWidth)
            .fillMaxHeight()
            .navigationBarsPadding(),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.inbox_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onPrimary,
            )
            Text(
                text = stringResource(
                    if (state.groupMode) Res.string.new_group_title else Res.string.new_message_title,
                ),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onPrimary,
                modifier = Modifier.weight(1f),
            )
            AppIconButton(
                iconName = if (state.groupMode) "person" else "group_add",
                contentDescription = stringResource(
                    if (state.groupMode) Res.string.new_message_title else Res.string.new_group_title,
                ),
                onClick = onToggleGroupMode,
                tint = AppTheme.colors.onPrimary,
            )
        }

        LazyColumn(
            contentPadding = PaddingValues(
                start = AppTheme.spacing.lg,
                end = AppTheme.spacing.lg,
                top = AppTheme.spacing.md,
                bottom = AppTheme.spacing.xl,
            ),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            if (state.groupMode) {
                item {
                    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                        diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField(
                            value = state.groupTitle,
                            onValueChange = onGroupTitleChange,
                            label = stringResource(Res.string.new_group_name_label),
                            placeholder = stringResource(Res.string.new_group_name_placeholder),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                        )
                        if (state.groupMembers.isNotEmpty()) {
                            androidx.compose.foundation.layout.FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                            ) {
                                state.groupMembers.forEach { member ->
                                    diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip(
                                        label = member.displayName,
                                        selected = true,
                                        leadingIcon = "close",
                                        onClick = { onToggleGroupMember(member) },
                                    )
                                }
                            }
                        }
                        diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton(
                            text = stringResource(Res.string.new_group_create),
                            onClick = onCreateGroup,
                            enabled = state.groupTitle.isNotBlank() && state.groupMembers.isNotEmpty() && !state.creatingGroup,
                            loading = state.creatingGroup,
                            leadingIcon = "group_add",
                            modifier = Modifier.fillMaxWidth().padding(bottom = AppTheme.spacing.sm),
                        )
                    }
                }
            }
            item {
                AppSearchBar(
                    value = state.query,
                    onValueChange = onQueryChange,
                    placeholder = stringResource(Res.string.new_message_search_placeholder),
                    modifier = Modifier.padding(bottom = AppTheme.spacing.sm),
                )
            }
            when {
                state.results.isNotEmpty() -> items(count = state.results.size, key = { state.results[it].uid }) { index ->
                    val profile = state.results[index]
                    PersonRow(
                        profile = profile,
                        selected = state.groupMode && state.groupMembers.any { it.uid == profile.uid },
                        onClick = {
                            if (state.groupMode) onToggleGroupMember(profile) else onPickUser(profile)
                        },
                    )
                }
                state.query.trim().length >= 2 && !state.searching -> item {
                    EmptyState(title = stringResource(Res.string.new_message_empty), description = "")
                }
                else -> item {
                    EmptyState(title = stringResource(Res.string.new_message_hint), description = "")
                }
            }
        }
    }
}

@Composable
private fun PersonRow(profile: PublicProfile, onClick: () -> Unit, selected: Boolean = false) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .background(
                if (selected) AppTheme.colors.primaryContainer else AppTheme.colors.surface,
                AppTheme.shapes.card,
            )
            .padding(AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.PersonAvatar(
            id = profile.uid,
            name = profile.displayName,
            photoUrl = profile.photoUrl,
            size = AppTheme.sizes.avatarMd,
        )
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
            Text(
                text = profile.displayName,
                style = AppTheme.typography.bodyLg,
                color = if (selected) AppTheme.colors.onPrimary else AppTheme.colors.onSurface,
            )
            profile.city?.takeIf { it.isNotBlank() }?.let { city ->
                Text(
                    text = city,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    maxLines = 1,
                )
            }
        }
    }
}
